import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { FiDollarSign, FiShield, FiTrendingUp, FiUser, FiInfo, FiRefreshCw, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuthContext } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';

const SelectedAccountIndicator = ({ compact = false }) => {
  const { user } = useAuthContext();

  // State for standardized broker integration data (using StandardizedBrokerIntegration interface)
  const [brokerIntegration, setBrokerIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Privacy: account values are HIDDEN by default on every page load.
  // Session-only toggle — not persisted, so each load starts masked.
  const [isMasked, setIsMasked] = useState(true);

  // Toggle mask for the current session (resets to hidden on next page load)
  const toggleMask = useCallback(() => {
    setIsMasked(prev => !prev);
  }, []);

  // Determine if integration is currently connected based on StandardizedBrokerIntegration status
  const isIntegrationConnected = (integration) => {
    if (!integration) return false;

    // Check status field from StandardizedBrokerIntegration
    if (integration.status !== 'active') return false;

    // Check if we have valid credentials
    if (!integration.credentials?.accessToken) return false;

    // Check token expiration
    if (integration.credentials?.expiresAt) {
      const expiryDate = new Date(integration.credentials.expiresAt);
      if (expiryDate <= new Date()) return false;
    }

    return integration.credentials?.isConnected === true;
  };

  // Fetch broker integrations directly from Firestore (StandardizedBrokerIntegration format)
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    console.log('🔍 SelectedAccountIndicator: Loading StandardizedBrokerIntegration for user:', user.uid);

    // Set up real-time listener for broker integrations
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'tbl_broker_integration'),
        where('userId', '==', user.uid)
      ),
      (snapshot) => {
        try {
          if (snapshot.empty) {
            console.log('ℹ️ No broker integrations found');
            setBrokerIntegration(null);
            setError(null);
            setLoading(false);
            return;
          }

          // Use StandardizedBrokerIntegration data directly (no mapping needed)
          const integrations = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Filter for connected integrations based on StandardizedBrokerIntegration structure
          const activeIntegrations = integrations.filter(integration =>
            isIntegrationConnected(integration)
          );

          console.log('🔍 SelectedAccountIndicator: Integration analysis:', {
            totalIntegrations: integrations.length,
            activeIntegrations: activeIntegrations.length,
            integrationStatuses: integrations.map(i => ({
              broker: i.broker,
              status: i.status,
              isConnected: i.credentials?.isConnected,
              hasToken: !!i.credentials?.accessToken,
              expiresAt: i.credentials?.expiresAt
            }))
          });

          if (activeIntegrations.length === 0) {
            console.log('ℹ️ No active broker integrations found');
            setBrokerIntegration(null);
            setError(null);
            setLoading(false);
            return;
          }

          // Sort by broker preference: tastytrade, tradier, then others
          const sortedIntegrations = activeIntegrations.sort((a, b) => {
            const preference = { 'tastytrade': 1, 'tradier': 2 };
            return (preference[a.broker] || 99) - (preference[b.broker] || 99);
          });

          const selectedIntegration = sortedIntegrations[0];
          console.log('✅ Found active broker integration (StandardizedBrokerIntegration):', {
            broker: selectedIntegration.broker,
            accountId: selectedIntegration.credentials?.selectedAccountId,
            status: selectedIntegration.status,
            isConnected: selectedIntegration.credentials?.isConnected,
            hasBalanceData: !!selectedIntegration.metadata?.balanceData,
            expiresAt: selectedIntegration.credentials?.expiresAt
          });

          setBrokerIntegration(selectedIntegration);
          setError(null);
        } catch (err) {
          console.error('❌ Error processing StandardizedBrokerIntegration:', err);
          setError('Failed to load account information');
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('❌ Firestore listener error:', err);
        setError('Connection error - please refresh');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Debouncing ref to prevent multiple calls
  const refreshTimeoutRef = useRef(null);

  // Manual refresh function - triggers a balance refresh via API (using StandardizedBrokerIntegration)
  const handleRefreshBalance = useCallback(async () => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Debounce the refresh call
    refreshTimeoutRef.current = setTimeout(async () => {
      if (!brokerIntegration || !brokerIntegration.credentials?.selectedAccountId) {
        console.warn('⚠️ No StandardizedBrokerIntegration or selected account for balance refresh');
        return;
      }

      if (isRefreshing) {
        console.log('🔄 Balance refresh already in progress, skipping...');
        return;
      }

      try {
        setIsRefreshing(true);
        setError(null);

        const { broker, credentials } = brokerIntegration;
        console.log(`🔄 Refreshing balance for ${broker} account:`, credentials.selectedAccountId);

        // Call the appropriate broker service to refresh balance
        const baseUrl = 'https://us-central1-quantum-flo-auto-algo-d3c2b.cloudfunctions.net';

        const response = await fetch(`${baseUrl}/fetch_account_balances`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await user.getIdToken()}`
          },
          body: JSON.stringify({
            userId: user.uid,
            broker: broker,
            accountId: credentials.selectedAccountId
          })
        });

        if (!response.ok) {
          throw new Error(`Balance refresh failed: ${response.status}`);
        }

        console.log('✅ Balance refresh successful - StandardizedBrokerIntegration will update via Firestore listener');
      } catch (error) {
        console.error('❌ Balance refresh error:', error);
        setError('Failed to refresh balance');
      } finally {
        setIsRefreshing(false);
      }
    }, 500); // 500ms debounce
  }, [brokerIntegration, user, isRefreshing]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to safely parse numeric values
  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value.replace(/[^0-9.-]/g, ''));
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  // Extract account data from StandardizedBrokerIntegration (no mapping needed)
  const getAccountData = () => {
    if (!brokerIntegration) return null;

    const { credentials, metadata, broker } = brokerIntegration;

    return {
      broker,
      accountId: credentials?.selectedAccountId,
      accountType: credentials?.accountType || metadata?.accountType || 'live',
      balanceData: metadata?.balanceData,
      lastBalanceRefresh: metadata?.lastBalanceRefresh
    };
  };

  const getBrokerDisplayName = (broker) => {
    const displayNames = {
      'tradier': 'Tradier',
      'tastytrade': 'TastyTrade',
      'schwab': 'Schwab',
      'interactive_brokers': 'IBKR'
    };
    return displayNames[broker] || broker;
  };

  const getAccountTypeIcon = (type) => {
    if (type === 'paper' || type === 'sandbox') {
      return <FiShield className="w-4 h-4 text-yellow-500" />;
    } else if (type === 'margin') {
      return <FiTrendingUp className="w-4 h-4 text-purple-500" />;
    }
    return <FiDollarSign className="w-4 h-4 text-green-500" />;
  };

  const formatCurrency = useMemo(() => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return (value, masked = false) => {
      if (masked) return '****';
      if (value === null || value === undefined || isNaN(value)) return '-';
      return formatter.format(value);
    };
  }, []);

  // Mask account ID
  const maskAccountId = (id) => {
    if (!id) return 'N/A';
    if (isMasked) return '****';
    return id;
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="bg-gray-900 rounded-lg border border-gray-700 p-4 mb-4">
        <div className="flex items-center gap-2 text-gray-400">
          <FiUser className="w-4 h-4" />
          <span className="text-sm">Loading account information...</span>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="bg-gray-900 rounded-lg border border-red-600 p-4 mb-4">
        <div className="flex items-center gap-2 text-red-400">
          <FiInfo className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  // Handle no broker integration
  if (!brokerIntegration) {
    return (
      <div className="bg-gray-900 rounded-lg border border-gray-700 p-4 mb-4">
        <div className="flex items-center gap-2 text-gray-500">
          <FiUser className="w-4 h-4" />
          <span className="text-sm">No broker account connected</span>
        </div>
      </div>
    );
  }

  // Get standardized account data
  const accountData = getAccountData();
  const { broker, accountId, accountType, balanceData } = accountData;

  // Parse balance values from StandardizedBalanceData (Single Source of Truth)
  // Backend now maps these correctly, so use the standardized fields directly
  const cashBalance = parseNumber(balanceData?.cash_balance);
  // Map Buying Power to derivative_buying_power (already standardized by backend)
  const buyingPower = parseNumber(balanceData?.derivative_buying_power);
  const optionBuyingPower = parseNumber(balanceData?.derivative_buying_power);
  const equityBuyingPower = parseNumber(balanceData?.equity_buying_power);

  console.log('🎯 SelectedAccountIndicator balance data (Single Source of Truth):', {
    broker: broker,
    accountId: accountId,
    balanceData: balanceData,
    cashBalance,
    buyingPower,
    optionBuyingPower,
    equityBuyingPower,
    lastRefresh: balanceData?.fetched_at
  });

  // Compact view for header/navbar
  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-1 bg-gray-800 rounded-lg border border-gray-700">
        {getAccountTypeIcon(accountType)}
        <span className="text-sm text-gray-300">
          {getBrokerDisplayName(broker)}
        </span>
        <span className="text-sm font-mono text-white">
          {maskAccountId(accountId)}
        </span>
        {cashBalance !== null && (
          <span className="text-sm text-green-400">
            {formatCurrency(cashBalance, isMasked)}
          </span>
        )}
      </div>
    );
  }

  // Full view for dashboard
  return (
    <div className="bg-gray-900 rounded-lg border border-purple-600 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <FiUser className="w-5 h-5 text-purple-500" />
          Active Trading Account
        </h3>
        <div className="flex items-center gap-2">
          {/* Mask Toggle Button */}
          <button
            onClick={toggleMask}
            data-testid="account-mask-toggle"
            aria-label={isMasked ? 'Show account values' : 'Hide account values'}
            aria-pressed={!isMasked}
            className="p-1.5 hover:bg-gray-700 rounded transition-colors"
            title={isMasked ? 'Show values' : 'Hide values'}
          >
            {isMasked ? (
              <FiEyeOff className="w-4 h-4 text-gray-400 hover:text-purple-400" />
            ) : (
              <FiEye className="w-4 h-4 text-gray-400 hover:text-purple-400" />
            )}
          </button>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <FiInfo className="w-3.5 h-3.5 flex-shrink-0" />
            <span>To change account, reconnect broker</span>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          {getAccountTypeIcon(accountType)}
          <span className="font-medium text-white">{getBrokerDisplayName(broker)}</span>
          <span className="text-xs px-2 py-1 bg-purple-900 text-purple-300 rounded">
            {accountType?.toUpperCase()}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-gray-400 mb-1">Account</p>
            <p className="font-mono text-white">{maskAccountId(accountId)}</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-gray-400">Balance</p>
              <button
                onClick={handleRefreshBalance}
                disabled={isRefreshing}
                className="p-1 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                title="Refresh balance from broker"
              >
                <FiRefreshCw className={`w-3.5 h-3.5 text-gray-400 hover:text-purple-400 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <p className="text-green-400 font-medium">
              {cashBalance !== null ? formatCurrency(cashBalance, isMasked) : '-'}
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-gray-400 mb-1">Buying Power</p>
            <p className="text-blue-400 font-medium">
              {buyingPower !== null ? formatCurrency(buyingPower, isMasked) : '-'}
            </p>
          </div>
          {balanceData?.fetched_at && (
            <div className="col-span-2 text-xs text-gray-500">
              Last updated: {new Date(balanceData.fetched_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectedAccountIndicator;